import { yupResolver } from "@hookform/resolvers/yup";
import { DialogContent } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useApiServiceCallback } from "../../../../../server/api/client";
import type { UUID } from "../../../../../utils/uuid";
import type { ChannelName } from "../../../../channels/types";
import { AdditionalRecipients } from "../../../../recipients/components/additional-recipients";
import { useRecipientsForChannel } from "../../../../recipients/hooks/use-recipients-for-channel";
import { MomentService } from "../../../client";
import { testSendRecipientSchema } from "../../../types";
import { SendTestDialogButtons } from "./send-test-dialog-buttons";

export const SendTestToRecipient: FC<{
  id: UUID;
  setSaving: (value: boolean) => void;
  setTestDialog: (value: boolean) => void;
  publishing: boolean;
  channel: ChannelName;
  children?: ReactNode;
}> = ({ id, setSaving, setTestDialog, publishing, channel, children }) => {
  const loader = useRecipientsForChannel(channel, false);
  const { data: recipientData } = loader;

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<{ recipients: UUID[] }>({
    defaultValues: { recipients: [] },
    resolver: yupResolver(testSendRecipientSchema),
  });

  const send = useApiServiceCallback(
    MomentService,
    async ({ apiService }, recipients: UUID[]) => {
      return await apiService.sendTest(
        id,
        recipientData?.filter((r) => recipients.includes(r.id)).map((r) => r.destination) ?? [],
        true,
      );
    },
    [id, recipientData],
  );

  const onSubmit = useCallback(
    async ({ recipients }: { recipients: UUID[] }): Promise<void> => {
      const { error } = await send(recipients);
      setSaving(false);
      if (error) {
        toast.error("Unable to send test Communication, please try again.");
        console.error(error);
      } else {
        toast("Test Communication sent");
        setTestDialog(false);
      }
    },
    [send, setSaving, setTestDialog],
  );

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
    >
      <DialogContent sx={{ minHeight: "200px" }}>
        {children}
        <Controller
          name="recipients"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AdditionalRecipients
              value={field.value}
              channel={channel}
              onChange={field.onChange}
              error={error?.message}
              maxAutocompleteHeight={115}
              recipientsLoader={loader}
            />
          )}
        />
      </DialogContent>
      <SendTestDialogButtons
        setSaving={setSaving}
        setTestDialog={setTestDialog}
        publishing={publishing}
        isSubmitting={isSubmitting}
      />
    </form>
  );
};
